import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  UgcEligibilityResponse,
  UgcProgramStatus
} from '../../../shared/object/xplorer-be-parsed-classes';
import { MetaDataService } from '../../../services/meta/meta-data.service';
import { UgcService } from '../../../services/ugc.service';

@Component({
  selector: 'xplorer-bowser-ugc-program-page',
  templateUrl: './ugc-program-page.component.html',
  styleUrl: './ugc-program-page.component.css',
  imports: [RouterLink, TranslateModule]
})
export class UgcProgramPageComponent {
  readonly isLoadingEligibility = signal(true);
  readonly isSubmitting = signal(false);
  readonly eligibility = signal<UgcEligibilityResponse | null>(null);
  readonly eligibilityError = signal(false);
  readonly acceptError = signal(false);

  readonly programStatus = computed<UgcProgramStatus | null>(() => this.eligibility()?.programStatus ?? null);
  readonly canAccept = computed(() => {
    const status = this.programStatus();
    return status === 'INVITED' || status === 'ELIGIBLE';
  });
  readonly isAccepted = computed(() => this.programStatus() === 'ACCEPTED');
  readonly isAcceptedAndAllowed = computed(() => this.isAccepted() && this.eligibility()?.allowed === true);
  readonly isAcceptedButBlocked = computed(() => this.isAccepted() && this.eligibility()?.allowed === false);
  readonly isWaiting = computed(() => this.programStatus() === 'WAITING');
  readonly blockingReasonKeys = computed(() => {
    const eligibility = this.eligibility();
    if (!eligibility || eligibility.allowed) {
      return [];
    }

    return this.resolveBlockingReasonKeys(eligibility);
  });

  private readonly ugcService = inject(UgcService);
  private readonly metaDataService = inject(MetaDataService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.metaDataService.setDefaultMetaData();
    this.loadEligibility();
  }

  acceptProgram(): void {
    if (!this.canAccept() || this.isSubmitting()) {
      return;
    }

    this.acceptError.set(false);
    this.isSubmitting.set(true);

    this.ugcService.acceptProgram()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (eligibility) => {
          this.eligibility.set(eligibility);
          this.isSubmitting.set(false);
          this.router.navigateByUrl('/ugc/new');
        },
        error: () => {
          this.acceptError.set(true);
          this.isSubmitting.set(false);
        }
      });
  }

  getStatusLabelKey(): string {
    const status = this.programStatus();

    switch (status) {
      case 'INVITED':
        return 'ugc_program.status.invited';
      case 'ELIGIBLE':
        return 'ugc_program.status.eligible';
      case 'ACCEPTED':
        return 'ugc_program.status.accepted';
      default:
        return 'ugc_program.status.waiting';
    }
  }

  getInactiveEyebrowKey(): string {
    return this.isAcceptedButBlocked()
      ? 'ugc_program.blocked.eyebrow'
      : 'ugc_program.waiting.eyebrow';
  }

  getInactiveTitleKey(): string {
    return this.isAcceptedButBlocked()
      ? 'ugc_program.blocked.title'
      : 'ugc_program.waiting.title';
  }

  getInactiveBodyKey(): string {
    return this.isAcceptedButBlocked()
      ? 'ugc_program.blocked.body'
      : 'ugc_program.waiting.body';
  }

  private loadEligibility(): void {
    this.ugcService.getEligibility()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (eligibility) => {
          this.eligibility.set(eligibility);
          this.eligibilityError.set(false);
          this.isLoadingEligibility.set(false);
        },
        error: () => {
          this.eligibilityError.set(true);
          this.isLoadingEligibility.set(false);
        }
      });
  }

  private resolveBlockingReasonKeys(eligibility: UgcEligibilityResponse): string[] {
    const reasons: string[] = [];
    const currentReputation = eligibility.currentReputation ?? 0;
    const minimumRequiredReputation = eligibility.minimumRequiredReputation ?? 0;
    const pendingSubmissions = eligibility.pendingSubmissions ?? 0;
    const maxPendingSubmissions = eligibility.maxPendingSubmissions ?? 0;

    if (minimumRequiredReputation > 0 && currentReputation < minimumRequiredReputation) {
      reasons.push('ugc_program.blocked.reasons.reputation_too_low');
    }

    if (maxPendingSubmissions > 0 && pendingSubmissions >= maxPendingSubmissions) {
      reasons.push('ugc_program.blocked.reasons.pending_limit_reached');
    }

    if (!reasons.length) {
      reasons.push('ugc_program.blocked.reasons.generic');
    }

    return reasons;
  }
}
